import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { useAuth } from '../context/AuthContext';
import { NofeeNavLogo } from '../components/NoFeeLogo';
import MenuShareCard from '../components/MenuShareCard';
import MoneyInput from '../components/MoneyInput';
import {
  Check,
  ChevronRight,
  ChevronLeft,
  Store,
  UtensilsCrossed,
  Share2,
  Plus,
  Trash2,
  Copy,
  Download,
  ExternalLink,
  Sparkles,
  ArrowRight,
} from 'lucide-react';

const STEPS = [
  { id: 'profile', label: 'Restaurant Profile', Icon: Store },
  { id: 'menu', label: 'Your First Menu', Icon: UtensilsCrossed },
  { id: 'share', label: 'Share & QR Code', Icon: Share2 },
];

const OnboardingWizard = () => {
  const { restaurant, token, api, fetchMe, loading } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Step 1 — profile
  const [profile, setProfile] = useState({
    address: '',
    phone: '',
    description: '',
    estimated_time: '30-45 min',
    delivery_fee: 0,
    min_order: 0,
  });
  const [profileHydrated, setProfileHydrated] = useState(false);

  // Step 2 — menu items (start with 3 empty slots)
  const [categoryId, setCategoryId] = useState(null);
  const [items, setItems] = useState([
    { name: '', price: '', description: '' },
    { name: '', price: '', description: '' },
    { name: '', price: '', description: '' },
  ]);

  useEffect(() => {
    if (!token && !loading) navigate('/login');
  }, [token, loading, navigate]);

  useEffect(() => {
    // Hydrate profile from server data ONCE, so navigating Back doesn't wipe user input
    if (restaurant && !profileHydrated) {
      setProfile((p) => ({
        ...p,
        address: restaurant.address || '',
        phone: restaurant.phone || '',
        description: restaurant.description || '',
        estimated_time: restaurant.estimated_time || '30-45 min',
        delivery_fee: restaurant.delivery_fee ?? 0,
        min_order: restaurant.min_order ?? 0,
      }));
      setProfileHydrated(true);
    }
  }, [restaurant, profileHydrated]);

  const publicMenuUrl = useMemo(() => {
    if (!restaurant) return '';
    // Use /menu/:slug (dedicated public menu route) — never /r/:slug which
    // collides with the ambassador referral codes route.
    return `${window.location.origin}/menu/${restaurant.slug}`;
  }, [restaurant]);

  if (loading || !restaurant) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-gold-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const saveProfile = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put(`/restaurants/${restaurant.id}`, {
        address: profile.address,
        phone: profile.phone,
        description: profile.description,
        estimated_time: profile.estimated_time,
        delivery_fee: parseFloat(profile.delivery_fee) || 0,
        min_order: parseFloat(profile.min_order) || 0,
      });
      await fetchMe();
      setStep(1);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save profile.');
    } finally {
      setSaving(false);
    }
  };

  const saveMenu = async () => {
    setSaving(true);
    setError('');
    try {
      // 1. Ensure we have a default category
      let catId = categoryId;
      if (!catId) {
        const existingCats = await api.get(`/restaurants/${restaurant.id}/categories`);
        if (existingCats.data && existingCats.data.length > 0) {
          catId = existingCats.data[0].id;
        } else {
          const newCat = await api.post(`/restaurants/${restaurant.id}/categories`, {
            name: 'Menu',
            order: 0,
            is_active: true,
          });
          catId = newCat.data.id;
        }
        setCategoryId(catId);
      }

      // 2. Create only filled-in items
      const valid = items.filter((i) => i.name.trim() && parseFloat(i.price) > 0);
      for (const it of valid) {
        await api.post(`/restaurants/${restaurant.id}/items`, {
          category_id: catId,
          name: it.name.trim(),
          price: parseFloat(it.price),
          description: it.description?.trim() || '',
        });
      }
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save menu items.');
    } finally {
      setSaving(false);
    }
  };

  const finish = () => navigate('/dashboard');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <NofeeNavLogo height={32} variant="dark" />
          <button
            onClick={finish}
            className="text-sm text-slate-500 hover:text-slate-800 transition-colors"
            data-testid="onboarding-skip"
          >
            Skip for now →
          </button>
        </div>
      </header>

      {/* Stepper */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
        <div className="flex items-center justify-between gap-2 mb-2">
          {STEPS.map((s, i) => {
            const done = i < step;
            const active = i === step;
            return (
              <React.Fragment key={s.id}>
                <div className="flex flex-col items-center flex-shrink-0" data-testid={`onboarding-step-indicator-${i}`}>
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                      done
                        ? 'bg-gold-500 text-white'
                        : active
                        ? 'bg-slate-900 text-white ring-4 ring-gold-500/30'
                        : 'bg-slate-200 text-slate-400'
                    }`}
                  >
                    {done ? <Check className="w-4 h-4" /> : i + 1}
                  </div>
                  <span
                    className={`mt-2 text-xs font-semibold text-center whitespace-nowrap ${
                      active ? 'text-slate-900' : 'text-slate-400'
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mb-6 ${done ? 'bg-gold-500' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Step body */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {error && (
          <div className="mb-6 p-3 bg-red-50 text-red-600 text-sm rounded-xl border border-red-100" data-testid="onboarding-error">
            {error}
          </div>
        )}

        {step === 0 && (
          <StepProfile
            profile={profile}
            setProfile={setProfile}
            restaurant={restaurant}
            onNext={saveProfile}
            saving={saving}
          />
        )}

        {step === 1 && (
          <StepMenu
            items={items}
            setItems={setItems}
            onBack={() => setStep(0)}
            onNext={saveMenu}
            saving={saving}
          />
        )}

        {step === 2 && (
          <StepShare
            restaurant={restaurant}
            publicMenuUrl={publicMenuUrl}
            onFinish={finish}
            onBack={() => setStep(1)}
          />
        )}
      </main>
    </div>
  );
};

/* ---------------------------------------------------------------------- */
/* Step 1: Profile                                                        */
/* ---------------------------------------------------------------------- */
const StepProfile = ({ profile, setProfile, restaurant, onNext, saving }) => {
  const update = (k, v) => setProfile({ ...profile, [k]: v });
  return (
    <Card>
      <CardHeader
        icon={Store}
        title="Tell customers about your restaurant"
        subtitle={`Welcome, ${restaurant.name}! These details show on your public menu page.`}
      />

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Address" full>
          <input
            value={profile.address}
            onChange={(e) => update('address', e.target.value)}
            placeholder="123 Main St, Orlando FL"
            className={INPUT_CLS}
            data-testid="onboarding-profile-address"
          />
        </Field>
        <Field label="Public phone">
          <input
            value={profile.phone}
            onChange={(e) => update('phone', e.target.value)}
            placeholder="+1 (555) 000-0000"
            className={INPUT_CLS}
            data-testid="onboarding-profile-phone"
          />
        </Field>
        <Field label="Estimated time">
          <input
            value={profile.estimated_time}
            onChange={(e) => update('estimated_time', e.target.value)}
            placeholder="30-45 min"
            className={INPUT_CLS}
            data-testid="onboarding-profile-time"
          />
        </Field>
        <Field label="Description" full>
          <textarea
            value={profile.description}
            onChange={(e) => update('description', e.target.value)}
            placeholder="Authentic family recipes since 1998. Italian, pizza & pasta."
            rows={3}
            className={INPUT_CLS}
            data-testid="onboarding-profile-description"
          />
        </Field>
        <Field label="Delivery fee ($)">
          <MoneyInput
            testid="onboarding-profile-delivery-fee"
            value={profile.delivery_fee}
            onChange={(raw) => update('delivery_fee', raw)}
            onCommit={(n) => update('delivery_fee', n === '' ? 0 : n)}
            inputClassName={INPUT_CLS}
            prefix=""
            placeholder="0.00"
          />
        </Field>
        <Field label="Minimum order ($)">
          <MoneyInput
            testid="onboarding-profile-min-order"
            value={profile.min_order}
            onChange={(raw) => update('min_order', raw)}
            onCommit={(n) => update('min_order', n === '' ? 0 : n)}
            inputClassName={INPUT_CLS}
            prefix=""
            placeholder="0.00"
          />
        </Field>
      </div>

      <Footer>
        <span />
        <button
          onClick={onNext}
          disabled={saving}
          className={PRIMARY_BTN}
          data-testid="onboarding-profile-next"
        >
          {saving ? 'Saving…' : 'Continue'} <ChevronRight className="w-4 h-4" />
        </button>
      </Footer>
    </Card>
  );
};

/* ---------------------------------------------------------------------- */
/* Step 2: Menu                                                           */
/* ---------------------------------------------------------------------- */
const StepMenu = ({ items, setItems, onBack, onNext, saving }) => {
  const update = (i, k, v) => {
    const next = [...items];
    next[i] = { ...next[i], [k]: v };
    setItems(next);
  };
  const removeRow = (i) => setItems(items.filter((_, idx) => idx !== i));
  const addRow = () => setItems([...items, { name: '', price: '', description: '' }]);

  const filledCount = items.filter((i) => i.name.trim() && parseFloat(i.price) > 0).length;

  return (
    <Card>
      <CardHeader
        icon={UtensilsCrossed}
        title="Add your first menu items"
        subtitle="Start with 3 of your bestsellers. You can add more later from the dashboard."
      />

      <div className="space-y-3">
        {items.map((it, i) => (
          <div
            key={i}
            className="grid grid-cols-12 gap-3 items-start bg-slate-50 rounded-xl p-3 border border-slate-200"
            data-testid={`onboarding-menu-row-${i}`}
          >
            <div className="col-span-12 sm:col-span-6">
              <input
                value={it.name}
                onChange={(e) => update(i, 'name', e.target.value)}
                placeholder="Item name (e.g., Margherita Pizza)"
                className={INPUT_CLS}
                data-testid={`onboarding-menu-name-${i}`}
              />
            </div>
            <div className="col-span-7 sm:col-span-2">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={it.price}
                  onChange={(e) => update(i, 'price', e.target.value)}
                  placeholder="12.99"
                  className={`${INPUT_CLS} pl-7`}
                  data-testid={`onboarding-menu-price-${i}`}
                />
              </div>
            </div>
            <div className="col-span-12 sm:col-span-3">
              <input
                value={it.description}
                onChange={(e) => update(i, 'description', e.target.value)}
                placeholder="Description (optional)"
                className={INPUT_CLS}
                data-testid={`onboarding-menu-desc-${i}`}
              />
            </div>
            <div className="col-span-5 sm:col-span-1 flex sm:justify-end">
              <button
                onClick={() => removeRow(i)}
                className="w-9 h-9 rounded-lg bg-white border border-slate-200 hover:border-red-300 hover:bg-red-50 text-slate-400 hover:text-red-500 flex items-center justify-center"
                aria-label="Remove item"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={addRow}
        className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-gold-600 hover:text-gold-700 transition-colors"
        data-testid="onboarding-menu-add"
      >
        <Plus className="w-4 h-4" /> Add another item
      </button>

      <Footer>
        <button onClick={onBack} className={SECONDARY_BTN} data-testid="onboarding-menu-back">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={onNext}
          disabled={saving || filledCount === 0}
          className={PRIMARY_BTN}
          data-testid="onboarding-menu-next"
        >
          {saving
            ? 'Saving…'
            : filledCount === 0
            ? 'Add at least 1 item'
            : `Save ${filledCount} item${filledCount > 1 ? 's' : ''}`}{' '}
          <ChevronRight className="w-4 h-4" />
        </button>
      </Footer>
    </Card>
  );
};

/* ---------------------------------------------------------------------- */
/* Step 3: Share + QR                                                     */
/* ---------------------------------------------------------------------- */
const StepShare = ({ restaurant, publicMenuUrl, onFinish, onBack }) => {
  const [copied, setCopied] = useState(false);

  const copyUrl = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(publicMenuUrl);
      } else {
        // Fallback for older browsers / insecure contexts
        const ta = document.createElement('textarea');
        ta.value = publicMenuUrl;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // Clipboard blocked (permission, insecure context, etc.) — show URL for manual copy
      window.prompt('Copy this link:', publicMenuUrl);
    }
  };

  const downloadQR = () => {
    const canvas = document.querySelector('canvas[data-testid="onboarding-qr-canvas"]');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `${restaurant.slug}-nofee-qr.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const whatsappShare = `https://wa.me/?text=${encodeURIComponent(
    `🍽️ ${restaurant.name} is now on NoFee! Order direct, no app needed: ${publicMenuUrl}`
  )}`;

  return (
    <Card>
      <CardHeader
        icon={Sparkles}
        title="You're live. Share your menu and start receiving orders."
        subtitle="Print the QR code at your tables, post the link on social, or send it to your customers."
      />

      <div className="grid md:grid-cols-2 gap-6">
        {/* QR */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 flex flex-col items-center text-center">
          <div className="bg-white p-3 rounded-2xl border-4 border-gold-500/20" data-testid="onboarding-qr-wrapper">
            <QRCodeCanvas
              value={publicMenuUrl}
              size={220}
              level="H"
              includeMargin={false}
              fgColor="#0a0a0a"
              bgColor="#ffffff"
              data-testid="onboarding-qr-canvas"
            />
          </div>
          <p className="text-xs text-slate-500 mt-4">Scan with any phone camera</p>
          <button
            onClick={downloadQR}
            className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-slate-900 hover:bg-slate-800 text-white rounded-xl transition-colors"
            data-testid="onboarding-qr-download"
          >
            <Download className="w-4 h-4" /> Download QR (PNG)
          </button>
        </div>

        {/* Link + Share buttons */}
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
              Share your menu
            </label>
            <MenuShareCard
              restaurantName={restaurant?.name || 'our restaurant'}
              menuUrl={publicMenuUrl}
              light
            />
          </div>

          <a
            href={publicMenuUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between gap-2 p-3 rounded-xl border border-slate-200 hover:border-gold-500 transition-colors group"
            data-testid="onboarding-preview-link"
          >
            <div>
              <div className="text-sm font-semibold text-slate-800">Preview your menu</div>
              <div className="text-xs text-slate-500">See what customers will see</div>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-gold-500 transition-colors" />
          </a>

          <div className="p-4 rounded-xl bg-gold-50 border border-gold-200">
            <h4 className="text-sm font-bold text-gold-900 mb-1">Pro tip</h4>
            <p className="text-xs text-gold-800 leading-relaxed">
              Print the QR on your menus, table tents, and receipts. Customers who scan order without fees — you keep 100%.
            </p>
          </div>
        </div>
      </div>

      <Footer>
        <button onClick={onBack} className={SECONDARY_BTN} data-testid="onboarding-share-back">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <button onClick={onFinish} className={PRIMARY_BTN} data-testid="onboarding-finish">
          Go to Dashboard <ChevronRight className="w-4 h-4" />
        </button>
      </Footer>
    </Card>
  );
};

/* ---------------------------------------------------------------------- */
/* Helpers                                                                */
/* ---------------------------------------------------------------------- */
const INPUT_CLS =
  'w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none transition-all text-sm bg-white';

const PRIMARY_BTN =
  'inline-flex items-center gap-1.5 px-5 py-2.5 bg-gold-500 hover:bg-gold-600 text-white font-bold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

const SECONDARY_BTN =
  'inline-flex items-center gap-1.5 px-4 py-2.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 font-semibold rounded-xl transition-colors';

const Card = ({ children }) => (
  <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6 sm:p-8">{children}</div>
);

const CardHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-3 mb-6">
    <div className="w-10 h-10 rounded-xl bg-gold-50 border border-gold-200 flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5 text-gold-600" />
    </div>
    <div>
      <h2 className="text-xl sm:text-2xl font-black text-slate-900">{title}</h2>
      <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
    </div>
  </div>
);

const Field = ({ label, children, full }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">{label}</label>
    {children}
  </div>
);

const Footer = ({ children }) => (
  <div className="flex items-center justify-between gap-3 mt-8 pt-6 border-t border-slate-100">{children}</div>
);

export default OnboardingWizard;
